import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { inr, today, formatApiError } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { askReason } from "../ApprovalLock";
import { field, label, toggleBtn, applyResult } from "./shared";

const toForm = (inv) => ({
  date: inv.date || today(),
  party_name: inv.party_name || "",
  vehicle_number: inv.vehicle_number || "",
  invoice_no: inv.invoice_no || "",
  invoice_type: inv.invoice_type || "Consolidated",
  source: inv.source || "tata_motors",
  gst: inv.gst !== false,
  gst_percent: String(inv.gst_percent || ""),
  amount: String(inv.base_amount != null ? inv.base_amount : inv.amount),
  discount: String(inv.discount || ""),
  description: inv.description || "",
  account_name: inv.account_name || "",
  challan_number: inv.challan_number || "",
  job_card_number: inv.job_card_number || "",
  driver_mobile: inv.driver_mobile || "",
});

const NO_PARTY_FROM = "2026-10-28";

const toPayload = (ef) => ({
  date: ef.date, party_name: ef.date >= NO_PARTY_FROM ? undefined : ef.party_name.trim(), vehicle_number: ef.vehicle_number,
  invoice_no: ef.invoice_no.trim(), invoice_type: ef.invoice_type, source: ef.source,
  description: ef.description, amount: Math.round(parseFloat(ef.amount) * 100) / 100,
  discount: Math.round((parseFloat(ef.discount) || 0) * 100) / 100,
  account_name: ef.account_name || "",
  challan_number: (ef.challan_number || "").trim(),
  job_card_number: (ef.job_card_number || "").trim().toUpperCase(),
  driver_mobile: ef.driver_mobile || "",
  gst: ef.source === "outside" ? ef.gst : true,
  gst_percent: ef.source === "outside" && ef.gst ? (parseFloat(ef.gst_percent) || 0) : 0,
});

const Field = ({ title, children }) => <div><label className={label}>{title}</label>{children}</div>;

const GstSection = ({ ef, set }) => {
  const amt = parseFloat(ef.amount) || 0;
  const pct = parseFloat(ef.gst_percent) || 0;
  return (
    <Field title="GST">
      <div className="grid grid-cols-2 gap-2">
        <button type="button" data-testid="edit-invoice-gst-yes" onClick={() => set({ gst: true })} className={`h-10 text-sm ${toggleBtn(ef.gst)}`}>GST</button>
        <button type="button" data-testid="edit-invoice-gst-no" onClick={() => set({ gst: false })} className={`h-10 text-sm ${toggleBtn(!ef.gst)}`}>Non-GST</button>
      </div>
      {ef.gst && (
        <div className="mt-2 flex items-center gap-2">
          <input data-testid="edit-invoice-gst-percent" type="number" step="0.01" min="0" className={field + " w-28"} value={ef.gst_percent}
            onChange={(e) => set({ gst_percent: e.target.value })} placeholder="GST %" />
          <span className="text-xs text-muted-foreground">GST ₹{((amt * pct) / 100).toFixed(2)} · Total ₹{(amt * (1 + pct / 100)).toFixed(2)}</span>
        </div>
      )}
    </Field>
  );
};

const EditLog = ({ log }) => {
  if (!log || log.length === 0) return null;
  return (
    <div className="rounded-md border border-border bg-muted/40 p-2.5">
      <div className="text-xs font-semibold mb-1">Edit history</div>
      <div className="space-y-1 max-h-28 overflow-y-auto">
        {log.slice().reverse().map((l, i) => (
          <div key={`${l.at}-${l.note}`} data-testid={`inv-edit-log-${i}`} className="text-[11px] text-muted-foreground num">{(l.at || "").slice(0, 16).replace("T", " ")} · {l.by_name || "user"} — {l.note}</div>
        ))}
      </div>
    </div>
  );
};

const IdentityFields = ({ ef, set, inp, partyOpts, accountOpts }) => (
  <>
    <div className="grid grid-cols-2 gap-3">
      <Field title="Invoice number"><input data-testid="edit-invoice-no" className={field} value={ef.invoice_no} onChange={inp("invoice_no")} /></Field>
      <Field title="Date"><input data-testid="edit-invoice-date" type="date" className={field + " num"} value={ef.date} onChange={inp("date")} /></Field>
    </div>
    {ef.date < NO_PARTY_FROM && <Field title="Party name">
      <input data-testid="edit-invoice-party" className={field} value={ef.party_name} onChange={inp("party_name")} list="edit-party-opts" />
      <datalist id="edit-party-opts">{partyOpts.map((p) => <option key={p} value={p} />)}</datalist>
    </Field>}
    <div>
      <label className={label}>Account name {ef.date >= NO_PARTY_FROM ? <span className="text-destructive">*</span> : <span className="text-muted-foreground font-normal">(credit account / group)</span>}</label>
      <input data-testid="edit-invoice-account" className={field} value={ef.account_name} onChange={inp("account_name")} list="edit-account-opts" placeholder={ef.date >= NO_PARTY_FROM ? "Account name" : "optional"} />
      <datalist id="edit-account-opts">{accountOpts.map((a) => <option key={a} value={a} />)}</datalist>
    </div>
    <Field title="Driver mobile">
      <input data-testid="edit-invoice-driver-mobile" inputMode="numeric" className={field + " num"} value={ef.driver_mobile}
        onChange={(e) => set({ driver_mobile: e.target.value.replace(/\D/g, "").slice(-10) })} placeholder="10-digit mobile (required for credit)" />
    </Field>
    <div className="grid grid-cols-2 gap-3">
      <Field title="Vehicle number"><input data-testid="edit-invoice-vehicle" className={field} value={ef.vehicle_number} onChange={inp("vehicle_number")} placeholder="optional" /></Field>
      <Field title="Challan number"><input data-testid="edit-invoice-challan" className={field} value={ef.challan_number} onChange={inp("challan_number")} placeholder="store bill no." /></Field>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <Field title="Job card / order number"><input data-testid="edit-invoice-jobcard" className={field + " num"} value={ef.job_card_number} onChange={inp("job_card_number")} placeholder="Job card no." /></Field>
      <Field title="Invoice type">
        <select data-testid="edit-invoice-type" className={field} value={ef.invoice_type} onChange={inp("invoice_type")}>
          {["Consolidated", "Spare Parts", "DEF", "Other"].map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </Field>
    </div>
    <Field title="Source">
      <div className="grid grid-cols-2 gap-2">
        {[{ v: "tata_motors", l: "Tata Motors" }, { v: "outside", l: "Outside" }].map((s) => (
          <button key={s.v} type="button" data-testid={`edit-invoice-source-${s.v}`} onClick={() => set({ source: s.v })} className={`h-10 text-sm ${toggleBtn(ef.source === s.v)}`}>{s.l}</button>
        ))}
      </div>
    </Field>
  </>
);

const AmountFields = ({ ef, set, inp }) => {
  const gstOn = ef.source === "outside" && ef.gst;
  const net = ((parseFloat(ef.amount) || 0) * (gstOn ? (1 + (parseFloat(ef.gst_percent) || 0) / 100) : 1)) - (parseFloat(ef.discount) || 0);
  return (
    <>
      <Field title={gstOn ? "Amount (before GST)" : "Amount (₹)"}>
        <input data-testid="edit-invoice-amount" type="number" step="0.01" min="0" className={field} value={ef.amount} onChange={inp("amount")} placeholder="0.00" />
      </Field>
      {ef.source === "outside" && <GstSection ef={ef} set={set} />}
      <div>
        <label className={label}>Discount (₹) <span className="text-muted-foreground font-normal text-xs">— adjust amount to close the bill</span></label>
        <input data-testid="edit-invoice-discount" type="number" step="0.01" min="0" className={field} value={ef.discount} onChange={inp("discount")} placeholder="0.00" />
        <p className="text-xs text-muted-foreground mt-1 num">Net receivable: {inr(net)}</p>
      </div>
      <Field title="Description"><input data-testid="edit-invoice-desc" className={field} value={ef.description} onChange={inp("description")} placeholder="Goods / service" /></Field>
    </>
  );
};

export const EditInvoiceDialog = ({ invoice, onClose, onChanged, user, reloadLocks, partyOpts, accountOpts }) => {
  const [ef, setEf] = useState(null);
  useEffect(() => { setEf(invoice ? toForm(invoice) : null); }, [invoice]);
  const set = (patch) => setEf((f) => ({ ...f, ...patch }));
  const inp = (k) => (e) => set({ [k]: e.target.value });

  const save = async () => {
    try {
      if (ef.date < NO_PARTY_FROM && !ef.party_name.trim()) return toast.error("Party name required");
      if (ef.date >= NO_PARTY_FROM && !ef.account_name.trim()) return toast.error("Account name required");
      if (ef.driver_mobile && ef.driver_mobile.length !== 10) return toast.error("Driver mobile must be 10 digits");
      if (!ef.invoice_no.trim()) return toast.error("Invoice number required");
      const amt = parseFloat(ef.amount);
      if (isNaN(amt) || amt < 0) return toast.error("Enter a valid amount (0 or more)");
      const payload = toPayload(ef);
      const edit_reason = askReason(user, "editing", `invoice ${invoice.invoice_no}`, true);
      if (edit_reason === null) return;
      const res = await api.put(`/invoices/${invoice.id}`, { ...payload, edit_reason });
      applyResult(res, `Invoice ${payload.invoice_no} updated`, reloadLocks);
      onClose(); onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={!!invoice} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl">Edit Invoice · {invoice?.invoice_no}</DialogTitle></DialogHeader>
        {invoice && ef && (
          <div className="space-y-3">
            {invoice.paid_amount > 0 && (
              <div className="rounded-md bg-muted p-2.5 text-xs text-muted-foreground">Already paid {inr(invoice.paid_amount)}. Editing the amount only recalculates the balance — recorded payments are not changed.</div>
            )}
            <IdentityFields ef={ef} set={set} inp={inp} partyOpts={partyOpts} accountOpts={accountOpts} />
            <AmountFields ef={ef} set={set} inp={inp} />
            <EditLog log={invoice.edit_log} />
          </div>
        )}
        <DialogFooter>
          <button data-testid="edit-invoice-save" onClick={save} className="h-10 px-5 rounded-md bg-accent text-accent-foreground font-medium hover:bg-accent/90 transition-colors">Save changes</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
